import React from 'react';
import { Link } from 'react-router-dom';
import {
    Shield,
    Cpu,
    Users,
    Zap,
    ArrowRight,
    CheckCircle2,
    Building,
    Cloud,
    Lock
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernSector.scss';

const ModernFederal = () => {
    const stats = [
        { number: '50+', label: 'Federal Agencies' },
        { number: '99.9%', label: 'System Uptime' },
        { number: '15+', label: 'Years Experience' },
        { number: '24/7', label: 'Support Available' }
    ];

    const services = [
        {
            icon: <Cpu size={32} />,
            title: 'IT Modernization',
            description: 'Modernizing legacy systems to meet evolving agency requirements.',
            features: ['Legacy System Migration', 'Cloud Infrastructure', 'System Integration', 'Performance Optimization']
        },
        {
            icon: <Users size={32} />,
            title: 'Workforce Experience',
            description: 'Creating user-focused solutions that improve agency operations.',
            features: ['User Experience Design', 'Workflow Optimization', 'Training Programs', 'Change Management']
        },
        {
            icon: <Zap size={32} />,
            title: 'Digital Transformation',
            description: 'Implementing innovative technologies to optimize government services.',
            features: ['Process Automation', 'Data Analytics', 'AI Integration', 'Digital Services']
        },
        {
            icon: <Shield size={32} />,
            title: 'Cybersecurity',
            description: 'Comprehensive security to protect critical federal data and infrastructure.',
            features: ['Security Assessment', 'Compliance Management', 'Threat Detection', 'Incident Response']
        }
    ];

    const capabilities = [
        'FedRAMP-aligned cloud solutions',
        'Zero trust architecture implementation',
        'Continuous monitoring and compliance',
        'Identity and access management',
        'Data protection and encryption',
        'Security operations center (SOC) services'
    ];

    const techOfferings = [
        { name: 'Cloud Services', icon: <Cloud size={24} /> },
        { name: 'Cybersecurity', icon: <Shield size={24} /> },
        { name: 'Infrastructure', icon: <Building size={24} /> },
        { name: 'Compliance', icon: <Lock size={24} /> }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Federal Civilian"
                subtitle="Empowering Federal Civilian Agencies Through Digital Innovation"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Federal Civilian' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-sector-hero">
                <div className="jk-sector-hero__container">
                    <div className="jk-sector-hero__grid">
                        <div className="jk-sector-hero__content">
                            <span className="jk-section-label">
                                <Building size={16} />
                                Federal Technology Solutions
                            </span>
                            <h2 className="jk-sector-hero__title">
                                Powering Digital Transformation for
                                <span className="highlight"> Federal Agencies</span>
                            </h2>
                            <p className="jk-sector-hero__description">
                                We partner with federal civilian agencies to deliver innovative technology
                                solutions that meet today's mission requirements while preparing for
                                tomorrow's challenges. Our expertise spans IT modernization, cybersecurity,
                                and digital transformation.
                            </p>
                            <div className="jk-sector-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Explore Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/about" className="jk-btn jk-btn--secondary">
                                    Learn More
                                </Link>
                            </div>
                        </div>

                        <div className="jk-sector-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-sector-hero__stat">
                                    <span className="jk-sector-hero__stat-number">{stat.number}</span>
                                    <span className="jk-sector-hero__stat-label">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-sector-services">
                <div className="jk-sector-services__container">
                    <div className="jk-sector-services__header">
                        <span className="jk-section-label">
                            <Cpu size={16} />
                            Our Core Services
                        </span>
                        <h2 className="jk-sector-services__title">
                            Comprehensive Technology
                            <span className="highlight"> Solutions</span>
                        </h2>
                        <p className="jk-sector-services__subtitle">
                            Technology solutions designed specifically for federal civilian agencies
                        </p>
                    </div>

                    <div className="jk-sector-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-sector-services__card">
                                <div className="jk-sector-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-sector-services__card-title">{service.title}</h3>
                                <p className="jk-sector-services__card-description">{service.description}</p>
                                <ul className="jk-sector-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-sector-capabilities">
                <div className="jk-sector-capabilities__container">
                    <div className="jk-sector-capabilities__grid">
                        <div className="jk-sector-capabilities__content">
                            <span className="jk-section-label">
                                <Shield size={16} />
                                Security & Compliance
                            </span>
                            <h2 className="jk-sector-capabilities__title">
                                Secure & Compliant
                                <span className="highlight"> Solutions</span>
                            </h2>
                            <p className="jk-sector-capabilities__text">
                                We deliver solutions that meet the stringent security and compliance
                                requirements of federal agencies, ensuring your data and systems are
                                protected while maintaining operational efficiency.
                            </p>

                            <ul className="jk-sector-capabilities__list">
                                {capabilities.map((item, index) => (
                                    <li key={index} className="jk-sector-capabilities__item">
                                        <CheckCircle2 size={20} className="jk-sector-capabilities__item-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/cybersecurity" className="jk-btn jk-btn--primary">
                                Learn About Our Security Solutions
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-sector-capabilities__visual">
                            <div className="jk-sector-capabilities__tech-grid">
                                {techOfferings.map((tech, index) => (
                                    <div key={index} className="jk-sector-capabilities__tech-card">
                                        <div className="jk-sector-capabilities__tech-icon">
                                            {tech.icon}
                                        </div>
                                        <span className="jk-sector-capabilities__tech-name">{tech.name}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="jk-sector-compliance">
                                <h3 className="jk-sector-compliance__title">Federal Compliance</h3>
                                <p className="jk-sector-compliance__text">
                                    All solutions meet or exceed federal security standards
                                </p>
                                <ul className="jk-sector-compliance__list">
                                    <li><CheckCircle2 size={16} /> FedRAMP-Ready Solutions</li>
                                    <li><CheckCircle2 size={16} /> FISMA-Aligned</li>
                                    <li><CheckCircle2 size={16} /> NIST 800-53 Aligned</li>
                                    <li><CheckCircle2 size={16} /> Section 508 Accessible</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernFederal;
